import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ABC_IDENTIFY } from "@/data/abcContent";
import { saveQuiz } from "@/lib/api";
import { ArrowRight, CheckCircle2, XCircle, Lightbulb } from "lucide-react";
import { toast } from "sonner";

export default function AbcIdentifyStep({ onDone, scenariosOverride, quizIdOverride }) {
  const scenarios = scenariosOverride || ABC_IDENTIFY.scenarios;
  const quizId = quizIdOverride || ABC_IDENTIFY.id;
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const allAnswered = Object.keys(answers).length === scenarios.length;

  const submit = async () => {
    if (!allAnswered) return;
    setSubmitting(true);
    try {
      const detailed = scenarios.map(s => ({
        question_id: s.id,
        selected: answers[s.id],
        correct: answers[s.id] === s.correct,
      }));
      const score = detailed.filter(a => a.correct).length;
      await saveQuiz(quizId, detailed, score);
      toast.success(`Puntuación: ${score}/${scenarios.length}`);
      onDone({ answers: detailed, score });
    } catch {
      toast.error("No pudimos guardar el ejercicio.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[var(--ha-text)]">{ABC_IDENTIFY.title}</h2>
      <p className="mt-3 text-[var(--ha-muted)] leading-relaxed max-w-2xl">{ABC_IDENTIFY.intro}</p>

      <div className="mt-8 space-y-5">
        {scenarios.map((s, i) => {
          const chosen = answers[s.id];
          return (
            <Card key={s.id} className="rounded-2xl border-[var(--ha-border)] bg-white">
              <CardContent className="p-6">
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Situación {i + 1}</p>
                <div className="mt-3 rounded-xl border border-[var(--ha-border)] bg-[var(--ha-bg)]/50 p-3">
                  <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Pensamiento (B)</p>
                  <p className="mt-1 italic text-[var(--ha-text)]">"{s.thought}"</p>
                </div>
                <div className="mt-2 rounded-xl border border-[var(--ha-border)] bg-[var(--ha-bg)]/50 p-3">
                  <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Consecuencia (C)</p>
                  <p className="mt-1 text-[var(--ha-text)]">{s.consequence}</p>
                </div>
                <p className="mt-4 text-sm font-semibold text-[var(--ha-text)]">¿Qué situación (A) crees que produjo este pensamiento y esta reacción?</p>
                <div className="mt-3 grid gap-2">
                  {s.options.map(o => {
                    const isSelected = chosen === o.key;
                    const isCorrect = o.key === s.correct;
                    const showFeedback = chosen !== undefined;
                    return (
                      <button
                        key={o.key}
                        data-testid={`abc-identify-${s.id}-${o.key}`}
                        onClick={() => setAnswers({ ...answers, [s.id]: o.key })}
                        className={`text-left rounded-xl border px-4 py-3 flex items-start gap-3 transition-colors ${
                          showFeedback && isSelected && isCorrect
                            ? "border-[var(--ha-success)] bg-[var(--ha-success)]/10"
                            : showFeedback && isSelected && !isCorrect
                            ? "border-[var(--ha-error)] bg-[var(--ha-error)]/10"
                            : "border-[var(--ha-border)] hover:bg-[var(--ha-bg)]"
                        }`}
                      >
                        <span className={`h-6 w-6 rounded-full border grid place-items-center text-xs font-bold shrink-0 ${isSelected ? "border-[var(--ha-primary)] text-[var(--ha-primary)] bg-white" : "border-[var(--ha-border)] text-[var(--ha-muted)]"}`}>{o.key}</span>
                        <span className="text-sm text-[var(--ha-text)] leading-relaxed">{o.text}</span>
                        {showFeedback && isSelected && (isCorrect ? <CheckCircle2 className="w-5 h-5 text-[var(--ha-success)] ml-auto" strokeWidth={1.75} /> : <XCircle className="w-5 h-5 text-[var(--ha-error)] ml-auto" strokeWidth={1.75} />)}
                      </button>
                    );
                  })}
                </div>
                {chosen !== undefined && s.explanation && (
                  <div
                    className="mt-4 rounded-xl border border-[var(--ha-primary)]/30 bg-[var(--ha-primary)]/5 p-4 flex gap-3"
                    data-testid={`abc-identify-${s.id}-explanation`}
                  >
                    <Lightbulb className="w-4.5 h-4.5 mt-0.5 text-[var(--ha-primary)] shrink-0" strokeWidth={1.75} />
                    <div>
                      <p className="text-xs uppercase tracking-[0.14em] font-bold text-[var(--ha-primary)]">Por qué</p>
                      <p className="mt-1 text-sm text-[var(--ha-text)] leading-relaxed">{s.explanation}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          data-testid="abc-identify-continue-btn"
          onClick={submit}
          disabled={submitting || !allAnswered}
          className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6"
        >
          Finalizar sesión <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
